import { useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconArrowUpRightOutline18 } from "nucleo-ui-outline-18"
import { useMedia } from "react-use"

import type { NotificationTarget } from "@productlane/lib/src/browser"
import { cn } from "@productlane/lib/src/cn"
import { formatRelativeTime } from "@productlane/lib/src/date"
import { Button, Empty, Loader } from "@productlane/ui"

import type { NotificationGroup } from "./group-notifications"
import type { NotificationListRow } from "./use-notifications"
import type { ThreadContextState } from "@/screens/app/threads/thread-context"
import {
	PANEL_SPECS,
	panelWidthStyle,
} from "@/components/panel-resize/panel-resize"
import { focusModeWidth } from "@/constants"
import { usePanelWidthState } from "@/lib/states/panel-width-state"
import { InboxCopilotEmptyState } from "@/screens/app/inbox/components/inbox-copilot-empty-state"
import { IssueDetailView } from "@/screens/app/issues/detail-screen"
import { ProjectDetailView } from "@/screens/app/projects/detail-screen"
import { CenterContent } from "@/screens/app/threads/components/center-content/center-content"
import { CopilotToggleButton } from "@/screens/app/threads/components/right-bar/ai-chat/copilot-toggle-button"
import { PersistentAiChatOverlay } from "@/screens/app/threads/components/right-bar/ai-chat/persistent-ai-chat-overlay"
import { RightBar } from "@/screens/app/threads/components/right-bar/right-bar"
import { useThreadDetailCore } from "@/screens/app/threads/hooks/useInitThread"
import { ThreadContextProvider } from "@/screens/app/threads/thread-context"
import { NOTIFICATIONS_COPY as copy } from "./copy"
import { notificationTypeIcon } from "./notification-icons"
import { notificationText } from "./notification-presentation"
import {
	notificationDestination,
	readNotificationTarget,
} from "./target-to-path"

/** Next/prev walk the notification list, so done, snooze and J/K move to the next notification. */
export interface NotificationNav {
	nextKey: string | null
	prevKey: string | null
	goNext: () => void
	goPrev: () => void
}

function NotificationThreadPane({
	threadId,
	nav,
}: {
	threadId: string
	nav: NotificationNav
}) {
	const isLarge = useMedia(focusModeWidth)
	const detailsWidth = usePanelWidthState((s) => s.widths["thread-details"])
	const core = useThreadDetailCore(threadId)
	const value: ThreadContextState = {
		...core,
		goNext: nav.goNext,
		goPrev: nav.goPrev,
		nextThread: nav.nextKey ? { id: nav.nextKey } : null,
		prevThread: nav.prevKey ? { id: nav.prevKey } : null,
		doneShortcutEnabled: false,
		// Esc clears the list selection here; leaving for /threads lost the page.
		leaveOnEscape: false,
	}

	if (!core.thread) {
		if (core.queryResult.type === "complete") {
			return <Empty className="h-full" title={copy.detail.notFound} text="" />
		}
		return (
			<div className="flex flex-1 items-center justify-center">
				<Loader size={12} />
			</div>
		)
	}

	return (
		<ThreadContextProvider value={value}>
			{/* RightBar sizes itself from this variable, so the column must set the same one. */}
			<div
				style={
					isLarge
						? panelWidthStyle(PANEL_SPECS["thread-details"], detailsWidth)
						: undefined
				}
				className={cn(
					"relative min-w-0 flex-1 overflow-hidden [--thread-details-width:20rem] min-[1440px]:[--thread-details-width:22rem]",
					isLarge
						? "grid grid-cols-[minmax(0,1fr)_var(--thread-details-width,22rem)]"
						: "flex",
				)}
			>
				<CenterContent embedded />
				<RightBar asDialog={!isLarge} />
				{/* On desktop the right bar's copilot button only switches tabs; this overlay is the copilot. */}
				{isLarge && <PersistentAiChatOverlay />}
			</div>
		</ThreadContextProvider>
	)
}

function NotificationSummaryPane({ row }: { row: NotificationListRow }) {
	const navigate = useNavigate()
	const TypeIcon = notificationTypeIcon(row.type)
	const { title, body } = notificationText(row)
	const destination = notificationDestination(row)
	const path = destination.kind === "page" ? destination.path : null

	return (
		<div className="relative flex flex-1 items-center justify-center overflow-y-auto p-8 pb-10">
			<div className="absolute right-3 top-3">
				{/* The notifications page binds Cmd J itself. */}
				<CopilotToggleButton
					scope={{ kind: "workspace" }}
					hideWhenOpen
					shortcut={false}
				/>
			</div>
			<div className="flex w-full max-w-md flex-col items-start gap-4">
				<span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-secondary dark:bg-gray-800">
					<TypeIcon className="h-5 w-5 flex-none" />
				</span>
				<div className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold tracking-tight text-primary">
						{title}
					</h2>
					{body && <p className="text-sm text-secondary">{body}</p>}
					<p className="text-xs text-tertiary">
						{formatRelativeTime(new Date(row.createdAt))}
					</p>
				</div>
				{path && (
					<Button
						variant="outline"
						iconRight={IconArrowUpRightOutline18}
						onClick={() => void navigate({ href: path })}
					>
						{copy.detail.open}
					</Button>
				)}
			</div>
		</div>
	)
}

const NO_TARGET: NotificationTarget = { kind: "none" }

export function NotificationDetail({
	group,
	sharedTarget,
	nav,
	onClose,
}: {
	group: NotificationGroup<NotificationListRow> | undefined
	/** Opens a group's target from its key alone, when the viewer has no notification in it. */
	sharedTarget?: NotificationTarget | null
	nav: NotificationNav
	onClose: () => void
}) {
	const latest = group?.rows[0]
	const payloadTarget = useMemo(
		() => readNotificationTarget(latest?.payload),
		[latest?.payload],
	)
	const target = (latest ? payloadTarget : sharedTarget) ?? NO_TARGET

	switch (target.kind) {
		case "thread":
			return <NotificationThreadPane threadId={target.threadId} nav={nav} />
		case "issue":
			return (
				<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
					<IssueDetailView
						key={target.issueId}
						id={target.issueId}
						variant="drawer"
						onClose={onClose}
					/>
				</div>
			)
		case "project":
			return (
				<div className="flex min-w-0 flex-1 flex-col overflow-hidden">
					<ProjectDetailView
						key={target.projectId}
						id={target.projectId}
						variant="drawer"
						onClose={onClose}
					/>
				</div>
			)
		default:
			return latest ? (
				<NotificationSummaryPane row={latest} />
			) : (
				<InboxCopilotEmptyState />
			)
	}
}
